import { z } from 'zod';
import { I18nStringSchema, MoneySchema } from './primitives.js';

/**
 * Product cross-sell — a physical/consumable item an operator sells alongside
 * a wash (a tin of wax, an air-freshener, a bottled drink). Lives at the
 * tenant library `tenants/{tid}/products/{id}`.
 *
 * DELIBERATELY NOT `addOns`. Structurally mirrors `AddOnSchema` (id / name /
 * description / active / excludedOutletIds) but priced with a FLAT `priceSen`
 * (a can of wax costs the same for a car or a bike) — NOT `pricingByVehicleType`
 * the way an add-on service is. There is intentionally NO stock / inventory
 * field: Kilat does not run a warehouse; the operator just lists what they can
 * hand over at the counter.
 *
 * Pro-gated + OFF by default. NOTHING is seeded — every tenant starts with an
 * empty `products` collection, so a tenant that does nothing sees + sells
 * nothing (zero migration). The two-condition gate lives in
 * `library/product-sales.ts` (`planAllowsProductSales` + the tenant
 * `productSales/config.enabled` toggle) and is enforced on the server
 * (bookTicket / appendItemsToTicket) — the client is never trusted.
 */
export const ProductSchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  priceSen: MoneySchema.describe('Flat unit price in sen. No per-vehicle-type pricing.'),
  active: z.boolean().default(true),
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this product is HIDDEN. Empty (default) = available everywhere.'),
  maxQtyPerOrder: z
    .number()
    .int()
    .positive()
    .max(99)
    .optional()
    .describe('Max units per booking. Absent or 1 = single unit (no quantity stepper).'),
});
export type Product = z.infer<typeof ProductSchema>;

/**
 * Per-tenant operator opt-in toggle for product cross-sell. Stored as the
 * single doc `tenants/{tid}/productSales/config`. Ships `enabled: false` so
 * the feature is OFF by default — the operator flips it on in the dashboard
 * Products editor (which is itself gated behind the Pro plan entitlement).
 *
 * This is HALF the gate: entitlement (`planAllowsProductSales`) is the other
 * half. The server requires BOTH true before it will price any product.
 */
export const ProductSalesConfigSchema = z.object({
  enabled: z
    .boolean()
    .default(false)
    .describe('Operator opt-in. False (default) = no products sold, even if the plan allows it.'),
});
export type ProductSalesConfig = z.infer<typeof ProductSalesConfigSchema>;

/** Firestore doc id of the single product-sales config document. */
export const PRODUCT_SALES_CONFIG_DOC_ID = 'config';
